import { RateConfResult } from '@numeo/types-core'
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    FormLabel,
    Textarea,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/joy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import React, { useState, useEffect } from 'react'
import { ValidationRequest } from '../types'
import { StructuredValidationForm } from './StructuredValidationForm'

interface ValidationFormProps {
    rateconfId: string
    projectName: string
    documentId: string
    fileUrl: string
    originalExtraction: RateConfResult
    onSubmit: (request: ValidationRequest) => Promise<void>
    loading: boolean
}

export const ValidationForm: React.FC<ValidationFormProps> = ({ rateconfId, projectName, documentId, fileUrl, originalExtraction, onSubmit, loading }) => {
    const [validationStatus, setValidationStatus] = useState<'pending' | 'accepted' | 'rejected'>('pending')
    const [corrections, setCorrections] = useState<string>(JSON.stringify(originalExtraction, null, 2))
    const [notes, setNotes] = useState<string>('')
    const [showCorrectionsForm, setShowCorrectionsForm] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [jsonError, setJsonError] = useState<string | null>(null)

    // Reset form state when rateconfId changes (new document loaded)
    useEffect(() => {
        setValidationStatus('pending')
        setShowCorrectionsForm(false)
        setCorrections(JSON.stringify(originalExtraction, null, 2))
        setNotes('')
        setError(null)
        setJsonError(null)
    }, [rateconfId, originalExtraction])

    const handleAccept = async () => {
        setError(null)
        setValidationStatus('accepted')

        try {
            await onSubmit({
                rateconfId,
                projectName,
                documentId,
                fileUrl,
                originalExtraction,
                status: 'accepted',
                notes: notes || undefined,
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to submit validation')
            setValidationStatus('pending')
        }
    }

    const handleReject = () => {
        setValidationStatus('rejected')
        setShowCorrectionsForm(true)
        setCorrections(JSON.stringify(originalExtraction, null, 2))
    }

    const handleSubmitRejection = async () => {
        setError(null)
        setJsonError(null)

        // Validate JSON
        try {
            const parsedCorrections = JSON.parse(corrections)

            await onSubmit({
                rateconfId,
                projectName,
                documentId,
                fileUrl,
                originalExtraction,
                status: 'rejected',
                corrections: parsedCorrections,
                notes: notes || undefined,
            })
        } catch (err) {
            if (err instanceof SyntaxError) {
                setJsonError('Invalid JSON format. Please fix the syntax errors.')
            } else {
                setError(err instanceof Error ? err.message : 'Failed to submit validation')
            }
        }
    }

    const handleCancelRejection = () => {
        setValidationStatus('pending')
        setShowCorrectionsForm(false)
        setCorrections(JSON.stringify(originalExtraction, null, 2))
        setNotes('')
        setJsonError(null)
    }

    if (validationStatus === 'pending' && !showCorrectionsForm) {
        return (
            <Card variant="outlined" sx={{ p: 3, mt: 3 }}>
                <Typography level="h4" sx={{ mb: 2 }}>
                    Validation
                </Typography>

                <Typography level="body-md" sx={{ mb: 3 }}>
                    Review the extracted fields above. Are they correct?
                </Typography>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <FormControl sx={{ mb: 3 }}>
                    <FormLabel>Notes (optional)</FormLabel>
                    <Textarea minRows={2} maxRows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes or comments..." />
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        color="success"
                        size="lg"
                        onClick={handleAccept}
                        disabled={loading}
                        startDecorator={loading ? <CircularProgress size="sm" /> : null}
                        sx={{ flex: 1 }}
                    >
                        {loading ? 'Submitting...' : 'Accept - Fields are Correct'}
                    </Button>

                    <Button color="danger" size="lg" onClick={handleReject} disabled={loading} sx={{ flex: 1 }}>
                        Reject - Fields Need Correction
                    </Button>
                </Box>
            </Card>
        )
    }

    if (showCorrectionsForm) {
        const handleStructuredFormSubmit = async (correctedData: RateConfResult) => {
            setError(null)

            try {
                await onSubmit({
                    rateconfId,
                    projectName,
                    documentId,
                    fileUrl,
                    originalExtraction,
                    status: 'rejected',
                    corrections: correctedData,
                    notes: notes || undefined,
                })
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to submit validation')
            }
        }

        return (
            <>
                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <StructuredValidationForm
                    originalExtraction={originalExtraction}
                    onSubmit={handleStructuredFormSubmit}
                    onCancel={handleCancelRejection}
                    loading={loading}
                />

                {/* Advanced JSON Editor (collapsed by default) */}
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography level="title-sm">Advanced: Raw JSON Editor</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                            For power users: Edit the raw JSON directly. Use the form above for easier editing.
                        </Typography>

                        {jsonError && (
                            <Alert color="warning" sx={{ mb: 2 }}>
                                {jsonError}
                            </Alert>
                        )}

                        <FormControl>
                            <Textarea
                                minRows={15}
                                maxRows={25}
                                value={corrections}
                                onChange={(e) => {
                                    setCorrections(e.target.value)
                                    setJsonError(null)
                                }}
                                placeholder="Edit the JSON data..."
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                }}
                            />
                        </FormControl>

                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button color="neutral" variant="outlined" onClick={handleCancelRejection} disabled={loading} sx={{ flex: 1 }}>
                                Cancel
                            </Button>

                            <Button
                                color="danger"
                                onClick={handleSubmitRejection}
                                disabled={loading}
                                startDecorator={loading ? <CircularProgress size="sm" /> : null}
                                sx={{ flex: 1 }}
                            >
                                {loading ? 'Submitting...' : 'Submit JSON Corrections'}
                            </Button>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </>
        )
    }

    return null
}
